import { ChangeEventHandler, FC, useMemo } from 'react';

import useSWR from 'swr';

import { SuspiciousUsersActionsColumn } from './suspiciousUsersTableColumns/SuspiciousUsersActionsColumn';
import { SuspiciousUsersFingerprintColumn } from './suspiciousUsersTableColumns/SuspiciousUsersFingerprintColumn';
import { SuspiciousUsersNicknamesColumn } from './suspiciousUsersTableColumns/SuspiciousUsersNicknamesColumn';

import css from './SuspiciousUsersPage.module.css';

import { userService } from 'v2/api/userService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { useFilter } from 'v2/hooks/useFilter';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { FingerprintFilter, FingerprintModel } from 'v2/types/FingerprintModel';

export const SuspiciousUsersPage: FC = () => {
  const { page, pageSize, onPageChange } = usePagination();

  const { filter, debouncedFilter, onChange } = useFilter<FingerprintFilter>({
    userNicknames: '',
  });

  const { data, mutate } = useSWR(
    ['getSuspiciousFingerprints', page, pageSize, debouncedFilter],
    userService.getSuspiciousFingerprints,
    { keepPreviousData: true }
  );

  const columns: TableColumn<FingerprintModel>[] = useMemo(
    () => [
      {
        key: 'fingerprintId',
        label: 'Fingerprint',
        render: (row) => <SuspiciousUsersFingerprintColumn fingerprint={row} />,
      },
      {
        key: 'userNicknames',
        label: 'User Nicknames',
        render: (row) => <SuspiciousUsersNicknamesColumn fingerprint={row} />,
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <SuspiciousUsersActionsColumn fingerprint={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange('userNicknames', e.target.value);
  };

  return (
    <PageLayout className={css.suspiciousUsersPage}>
      <PageTitle
        action={<SearchTextField value={filter.userNicknames} label="User Nickname" onChange={handleChange} />}
      >
        Suspicious Users
      </PageTitle>

      <DataTable
        columns={columns}
        data={data?.result}
        page={page}
        total={data?.total}
        pageSize={pageSize}
        onPageChange={onPageChange}
        withPagination
      />
    </PageLayout>
  );
};
